import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../interceptors";
import { baseUrl } from "../../config/config";

// Bell icon + count badge + dropdown showing pending approvals for the
// current user. Polls GET /job-posting/approvals/pending every 30 seconds.
//
// Click an item → quick Approve / Reject inline, or "View offer ↗" to
// jump to the offer detail page.
//
// Notification surface is scoped to approvals for v1; the same bell can
// later host email-bounce alerts, interview reminders, etc.
const POLL_INTERVAL_MS = 30000;

export default function NotificationBell() {
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const nav = useNavigate();
  const containerRef = useRef(null);

  const load = async () => {
    try {
      const r = await axiosInstance.get(`${baseUrl}/job-posting/approvals/pending`);
      setPending(Array.isArray(r.data?.approvals) ? r.data.approvals : []);
      setError(null);
    } catch (e) {
      // Silent on poll — only surface when the dropdown is actually open.
      if (open) setError(e?.message || "Couldn't load notifications");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const approve = async (a) => {
    setBusyId(a.id);
    try {
      await axiosInstance.post(`${baseUrl}/job-posting/approvals/${a.id}/approve`, {});
      await load();
    } catch (e) { setError(e?.data?.error || e?.message || "Approve failed"); }
    finally { setBusyId(null); }
  };

  const reject = async (a) => {
    const reason = window.prompt("Reason for rejection (required):");
    if (!reason || !reason.trim()) return;
    setBusyId(a.id);
    try {
      await axiosInstance.post(`${baseUrl}/job-posting/approvals/${a.id}/reject`, { reason });
      await load();
    } catch (e) { setError(e?.data?.error || e?.message || "Reject failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative text-vf-muted hover:text-vf-base"
        aria-label="Notifications"
      >
        🔔
        {pending.length > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
            {pending.length > 9 ? "9+" : pending.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-8 w-80 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50">
          <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
            <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold">Notifications</div>
            <button onClick={() => nav("/admin/approvals")} className="text-[11px] text-vf-primary hover:underline">
              See all
            </button>
          </div>

          {error && (
            <div className="px-3 py-2 text-[11px] text-red-700 bg-red-50 border-b border-red-100">{error}</div>
          )}

          {pending.length === 0 ? (
            <div className="p-6 text-center text-xs text-vf-muted">
              No pending approvals.
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
              {pending.map((a) => (
                <div key={a.id} className="p-3">
                  <div className="text-sm font-medium text-vf-base">
                    Offer #{a.offerId} · Step {a.stepOrder}
                  </div>
                  <div className="text-[11px] text-vf-muted mt-0.5">
                    Notified {a.notifiedAt ? timeAgo(a.notifiedAt) : "—"}
                  </div>
                  <div className="flex gap-1 mt-2">
                    <button
                      onClick={() => { setOpen(false); nav(`/admin/offers/${a.offerId}`); }}
                      className="px-2 py-0.5 rounded border border-slate-300 text-[11px]"
                    >
                      View ↗
                    </button>
                    <button
                      disabled={busyId === a.id}
                      onClick={() => approve(a)}
                      className="px-2 py-0.5 rounded bg-emerald-600 text-white text-[11px] disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      disabled={busyId === a.id}
                      onClick={() => reject(a)}
                      className="px-2 py-0.5 rounded border border-red-300 text-red-700 text-[11px] disabled:opacity-50"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function timeAgo(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "in the future";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
